import { useState, useContext, useEffect, useMemo } from 'react'
import _ from "lodash"

import { Typography, Tooltip, Button, Box } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'

import { PassContext } from '../pass-context'
import { AppContext } from '/providers/app-context'
import { urlsMatchIgnoringCacheBust } from '/components/editor/image-url'
import ImageSelector from '/components/image-selector'
import dataSelector from './data-selector'
import PopupShell from '../popup-shell'

const variablesPath = "images.google.hero"

const PopupDialogImage = ({ open, close, title, path, sources, saveFunction, stampcardUrl }) => {

  const { classData, objectData, variables, setVariables, passType, openVariablesManager } = useContext(PassContext)
  const { imageList } = useContext(AppContext)

  const [newClassData, setNewClassData] = useState({})
  const [newObjectData, setNewObjectData] = useState({})
  const [newVariables, setNewVariables] = useState({})
  const [requiredSize, setRequiredSize] = useState("")
  const [stampUrl, setStampUrl] = useState(stampcardUrl ?? "")

  const imageType = sources.path === "stampcard" ? "stampcard" : "hero"

  const currentImageUrl = useMemo(() => {
    if (!open) return null

    if (sources.path === "stampcard") return stampUrl

    const imageUrl = _.get(newVariables, variablesPath) || dataSelector({ objectData: newObjectData, variables: newVariables, path }) || dataSelector({ objectData: newClassData, variables: newVariables, path })
    const img = imageList.find(i => urlsMatchIgnoringCacheBust(i.s3_url, imageUrl))
    const url = img ? img.s3_url : imageUrl
    return url
  }, [open, path, newClassData, newObjectData, newVariables, sources.path, stampUrl, imageList])


  useEffect(() => {
    if (open) {
      setNewClassData(_.cloneDeep(classData))
      setStampUrl(stampcardUrl ?? "")
    }
  }, [open, classData, stampcardUrl])

  useEffect(() => {
    if (open) {
      setNewObjectData(_.cloneDeep(objectData))
    }
  }, [open, objectData])

  useEffect(() => {
    if (open) {
      setNewVariables(_.cloneDeep(variables))
    }
  }, [open, variables])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const removeImageFromTemplate = () => {
    setNewVariables(prev => {
      let nd = { ...prev }
      _.unset(nd, variablesPath)
      return nd
    })
  }

  const setImageUrl = imageUrl => {
    if (saveFunction) {
      setStampUrl(imageUrl)
    } else {
      setNewVariables(prev => {
        let nd = { ...prev }
        _.set(nd, variablesPath, imageUrl)
        return nd
      })
    }
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }

  const saveFormData = () => {

    if (saveFunction) {
      saveFunction(stampUrl)
    } else {

      if (!_.isEqual(newVariables, variables)) setVariables(newVariables)
    }

    close()
  }


  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  try {
    return (

      <PopupShell open={open} onClose={handleClose} width={1000} variant="solid">
        {/* Header */}
        <div className="flex-shrink-0 p-2">
          <div className="flex justify-between items-center">
            <Typography color="secondary" variant="h6">{title}<br />({requiredSize})</Typography>
            <Tooltip title="Press ESC to dismiss">
              <Button endIcon={<EastRoundedIcon />} onClick={handleClose}>Cancel</Button>
            </Tooltip>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row gap-5 p-2">
          <ImageSelector
            passType={passType}
            currentImageUrl={currentImageUrl}
            setImageUrl={setImageUrl}
            imageType={imageType}
            deleteAllowed={sources.path !== "stampcard"}
            removeImageFromTemplate={removeImageFromTemplate}
            setRequiredSize={setRequiredSize}
          />
        </div>

        {/* Footer */}
        <Box className="flex-shrink-0 p-3">
          <hr className="my-2" />
          <div className="flex flex-col md:flex-row justify-between items-center gap-2">
            <Button startIcon={<DatasetOutlinedIcon />} onClick={openVariablesManager} >Manage variables</Button>
            <Button variant="contained" onClick={saveFormData}>Save</Button>
          </div>
        </Box>

      </PopupShell>
    )
  }
  catch (err) {
    throw err
  }
}

export default PopupDialogImage
